import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useActiveTabId, useAppStore } from './store.js';

// Global keyboard shortcuts. Mounted once at the app root. None of the
// shortcuts fire while the user is typing in an input or contenteditable.
//
//   Cmd/Ctrl+K  — toggle command palette (handled by command-palette.tsx)
//   Cmd/Ctrl+T  — new tab
//   ?           — show the shortcuts overlay
//   Esc         — close the shortcuts overlay; otherwise clear the
//                  active tab's selection across all channels
//
// Keep the list short — every shortcut is an investment in muscle memory.

function isTypingTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable === true;
}

export interface HotkeyProviderProps {
  /** Toggle the command palette. Owning the open state in the parent
   *  guarantees one listener per shortcut even when the palette
   *  component itself uses the same hook elsewhere. */
  onOpenPalette?: () => void;
  /** Save current workspace (⌘S). Defaults to a no-op. */
  onSaveWorkspace?: () => void;
}

export function HotkeyProvider({ onOpenPalette, onSaveWorkspace }: HotkeyProviderProps = {}) {
  const { t } = useTranslation();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const activeTabId = useActiveTabId();
  const addTab = useAppStore((s) => s.actions.addTab);
  const clearSelection = useAppStore((s) => s.actions.clearSelection);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Cmd/Ctrl+K → palette (parent controls open state).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (!onOpenPalette) return;
        e.preventDefault();
        onOpenPalette();
        return;
      }

      // Cmd/Ctrl+S → save workspace; preventDefault so the browser
      // save-page dialog doesn't fire.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        if (!onSaveWorkspace) return;
        e.preventDefault();
        onSaveWorkspace();
        return;
      }

      // Cmd/Ctrl+T → new tab. Most browsers eat this; included for
      // power users with extensions that pass it through.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 't') {
        e.preventDefault();
        addTab({ title: t('tabBar.newTabTitle') });
        return;
      }

      if (isTypingTarget(e)) return;

      // Esc: close the overlay if it's open; otherwise clear the active
      // tab's selection across every channel.
      if (e.key === 'Escape') {
        if (overlayOpen) {
          setOverlayOpen(false);
        } else if (activeTabId) {
          clearSelection(activeTabId);
        }
        return;
      }

      // ? (Shift+/) → toggle the shortcuts overlay.
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setOverlayOpen((o) => !o);
        return;
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeTabId, addTab, clearSelection, overlayOpen, t, onOpenPalette, onSaveWorkspace]);

  return (
    <Dialog.Root open={overlayOpen} onOpenChange={setOverlayOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.32)',
            zIndex: 1499,
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          style={{
            position: 'fixed',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '420px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md, 6px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            zIndex: 1500,
            padding: 'var(--space-4)',
          }}
        >
          <Dialog.Title
            style={{
              margin: 0,
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {t('hotkeys.title')}
          </Dialog.Title>
          <table style={{ width: '100%', fontSize: 'var(--font-size-sm)' }}>
            <tbody>
              <ShortcutRow keys="⌘K / Ctrl+K" label={t('hotkeys.search')} />
              <ShortcutRow keys="⌘T / Ctrl+T" label={t('hotkeys.newTab')} />
              <ShortcutRow keys="Esc" label={t('hotkeys.escape')} />
              <ShortcutRow keys="?" label={t('hotkeys.help')} />
              <ShortcutRow keys="j / k" label={t('hotkeys.rowNav')} />
              <ShortcutRow keys="g / G" label={t('hotkeys.firstLast')} />
              <ShortcutRow keys="w" label={t('hotkeys.watchlist')} />
              <ShortcutRow keys="r" label={t('hotkeys.reject')} />
              <ShortcutRow keys="/" label={t('hotkeys.focusSearch')} />
            </tbody>
          </table>
          <p
            style={{
              marginTop: 'var(--space-3)',
              marginBottom: 0,
              color: 'var(--fg-muted)',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            {t('hotkeys.dragHint')}
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <tr>
      <td
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--fg-muted)',
          width: '40%',
          padding: '6px 0',
        }}
      >
        {keys}
      </td>
      <td style={{ padding: '6px 0' }}>{label}</td>
    </tr>
  );
}
